import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, CheckCircle, ArrowLeft, Building2, Mail, User, Server } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const PilotRequestPage = () => {
  const navigate = useNavigate();
  const { lang, setLanguage, t } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    company: '',
    role: '',
    nhi_count: '',
    identity_stack: '',
    deployment_preference: '',
    notes: ''
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.full_name || !form.company || !form.role || !form.nhi_count || !form.deployment_preference) {
      setError(t('pilot_required'));
      return;
    }
    setLoading(true);
    try {
      await api.post('/pilots/request', form);
      setSubmitted(true);
    } catch (err) {
      setError(t('pilot_error'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="max-w-lg w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#34C759]/10 border border-[#34C759]/30">
            <CheckCircle className="w-8 h-8 text-[#34C759]" />
          </div>
          <h1
            className="text-3xl font-bold text-[#F9FAFB] mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="pilot-confirmation-title"
          >
            {t('pilot_confirm_title')}
          </h1>
          <p className="text-[#9CA3AF] mb-2 leading-relaxed">
            {t('pilot_confirm_thanks')} <span className="text-[#F9FAFB] font-medium">{form.full_name}</span>.
          </p>
          <p className="text-[#9CA3AF] mb-8 leading-relaxed">
            {t('pilot_confirm_message')} <span className="text-[#F9FAFB] font-medium">{form.company}</span>.
          </p>
          <div className="enterprise-card p-6 text-left mb-8">
            <div className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-4">{t('pilot_confirm_summary')}</div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-[#6B7280]">{t('pilot_confirm_deployment')}</span>
                <span className="text-[#F9FAFB] font-mono">{form.deployment_preference}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7280]">{t('pilot_confirm_nhi')}</span>
                <span className="text-[#F9FAFB] font-mono">{form.nhi_count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#6B7280]">{t('pilot_confirm_email_sent')}</span>
                <span className="text-[#0066FF] font-mono text-xs">{form.email}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4">
            <Link to="/">
              <Button variant="outline" className="secondary-btn" data-testid="back-to-home-btn">
                {t('pilot_confirm_home')}
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button className="primary-btn" data-testid="explore-platform-btn">
                {t('pilot_confirm_explore')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#27272A]/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-[#0066FF]" />
            <span className="text-xl font-bold text-[#F9FAFB] tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Agent Control Plane
            </span>
          </Link>
          <Link to="/" className="text-[#9CA3AF] hover:text-[#F9FAFB] text-sm flex items-center gap-1 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('pilot_confirm_home')}
          </Link>
          <div className="flex items-center bg-[#1A1A1A] border border-[#27272A]/60 ml-3" data-testid="pilot-lang-selector">
            <button onClick={() => setLanguage('en')} className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${lang === 'en' ? 'bg-[#0066FF] text-white' : 'text-[#6B7280] hover:text-[#F9FAFB]'}`}>EN</button>
            <button onClick={() => setLanguage('es')} className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${lang === 'es' ? 'bg-[#0066FF] text-white' : 'text-[#6B7280] hover:text-[#F9FAFB]'}`}>ES</button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <h1
              className="text-3xl md:text-4xl font-bold text-[#F9FAFB] mb-3"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="pilot-form-title"
            >
              {t('pilot_title')}
            </h1>
            <p className="text-[#9CA3AF] leading-relaxed">
              {t('pilot_subtitle')}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="pilot-request-form">
            {/* Name + Email row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">
                  {t('pilot_name')} <span className="text-[#FF3B30]">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                  <Input
                    value={form.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    placeholder="Jane Smith"
                    className="pl-10 bg-[#121212] border-[#27272A] text-[#F9FAFB] placeholder:text-[#4B5563] focus:border-[#0066FF]"
                    data-testid="pilot-name-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">
                  {t('pilot_email')} <span className="text-[#FF3B30]">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="jane@company.com"
                    className="pl-10 bg-[#121212] border-[#27272A] text-[#F9FAFB] placeholder:text-[#4B5563] focus:border-[#0066FF]"
                    data-testid="pilot-email-input"
                  />
                </div>
              </div>
            </div>

            {/* Company + Role row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">
                  {t('pilot_company')} <span className="text-[#FF3B30]">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                  <Input
                    value={form.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="Acme Corp"
                    className="pl-10 bg-[#121212] border-[#27272A] text-[#F9FAFB] placeholder:text-[#4B5563] focus:border-[#0066FF]"
                    data-testid="pilot-company-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">
                  {t('pilot_role')} <span className="text-[#FF3B30]">*</span>
                </label>
                <Input
                  value={form.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  placeholder={t('pilot_role_placeholder')}
                  className="bg-[#121212] border-[#27272A] text-[#F9FAFB] placeholder:text-[#4B5563] focus:border-[#0066FF]"
                  data-testid="pilot-role-input"
                />
              </div>
            </div>

            {/* NHI Count + Deployment */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">
                  {t('pilot_nhi')} <span className="text-[#FF3B30]">*</span>
                </label>
                <Select value={form.nhi_count} onValueChange={(v) => handleChange('nhi_count', v)}>
                  <SelectTrigger className="bg-[#121212] border-[#27272A] text-[#F9FAFB]" data-testid="pilot-nhi-select">
                    <SelectValue placeholder={t('pilot_nhi_placeholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    {t('nhi_options').map((opt, i) => (
                      <SelectItem key={i} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">
                  {t('pilot_deployment')} <span className="text-[#FF3B30]">*</span>
                </label>
                <Select value={form.deployment_preference} onValueChange={(v) => handleChange('deployment_preference', v)}>
                  <SelectTrigger className="bg-[#121212] border-[#27272A] text-[#F9FAFB]" data-testid="pilot-deployment-select">
                    <SelectValue placeholder={t('pilot_deployment_placeholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    {t('deployment_options').map((opt, i) => (
                      <SelectItem key={i} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Identity Stack */}
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">
                {t('pilot_stack')}
                <span className="text-[#6B7280] ml-1">({t('pilot_optional')})</span>
              </label>
              <Input
                value={form.identity_stack}
                onChange={(e) => handleChange('identity_stack', e.target.value)}
                placeholder={t('pilot_stack_placeholder')}
                className="bg-[#121212] border-[#27272A] text-[#F9FAFB] placeholder:text-[#4B5563] focus:border-[#0066FF]"
                data-testid="pilot-stack-input"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">
                {t('pilot_notes')}
                <span className="text-[#6B7280] ml-1">({t('pilot_optional')})</span>
              </label>
              <Textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder={t('pilot_notes_placeholder')}
                rows={3}
                className="bg-[#121212] border-[#27272A] text-[#F9FAFB] placeholder:text-[#4B5563] focus:border-[#0066FF] resize-none"
                data-testid="pilot-notes-input"
              />
            </div>

            {error && (
              <div className="text-[#FF3B30] text-sm bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-3" data-testid="pilot-form-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="primary-btn w-full py-5 text-base"
              data-testid="pilot-submit-btn"
            >
              {loading ? t('pilot_submitting') : t('pilot_submit')}
              {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>

            <p className="text-[11px] text-[#6B7280] text-center leading-relaxed">
              {t('pilot_consent')}
            </p>
          </form>

          {/* Trust indicators */}
          <div className="mt-12 pt-8 border-t border-[#27272A]/40">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <Server className="w-5 h-5 text-[#0066FF] mx-auto mb-2" />
                <div className="text-xs text-[#9CA3AF]">{t('pilot_trust_1')}</div>
              </div>
              <div>
                <Shield className="w-5 h-5 text-[#34C759] mx-auto mb-2" />
                <div className="text-xs text-[#9CA3AF]">{t('pilot_trust_2')}</div>
              </div>
              <div>
                <CheckCircle className="w-5 h-5 text-[#FFCC00] mx-auto mb-2" />
                <div className="text-xs text-[#9CA3AF]">{t('pilot_trust_3')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PilotRequestPage;
